import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { UseManageEmailsProps } from './types';

const useManageEmails = ({ emails, setEmails }: UseManageEmailsProps) => {
  const [inputValue, setInputValue] = useState<{ [key: number]: string }>({});
  const { enqueueSnackbar } = useSnackbar();

  const handleEmailChange = useCallback((companyId: number, email: string) => {
    setInputValue(prev => ({
      ...prev,
      [companyId]: email,
    }));
  }, []);

  const validateEmail = useCallback(
    (email: string) => {
      const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/;
      if (!re.test(email)) {
        enqueueSnackbar('Invalid email address', { variant: 'error' });
      }
      return re.test(email);
    },
    [enqueueSnackbar]
  );

  const isEmailPending = useCallback(
    (companyId: number, email: string) => {
      const isPending = emails[companyId]?.includes(email);
      if (isPending) {
        enqueueSnackbar('Email is already pending', { variant: 'warning' });
      }
      return isPending;
    },
    [emails, enqueueSnackbar]
  );

  const handleAddEmail = useCallback(
    (companyId: number) => {
      const email = inputValue[companyId]?.trim();
      if (email && validateEmail(email) && !isEmailPending(companyId, email)) {
        setEmails((prev: { [key: number]: string[] }) => ({
          ...prev,
          [companyId]: [...(prev[companyId] || []), email],
        }));
      }
    },
    [inputValue, isEmailPending, setEmails, validateEmail]
  );

  const handleDeleteEmail = useCallback(
    (companyId: number, emailToDelete: string) => {
      setEmails(prev => ({
        ...prev,
        [companyId]: prev[companyId].filter(email => email !== emailToDelete),
      }));
    },
    [setEmails]
  );

  useEffect(() => {
    if (!emails) {
      return;
    }

    // Remove emails from input value if they are already added
    setInputValue(prev => {
      const newInputValue = { ...prev };
      Object.keys(emails).forEach(companyId => {
        if (Object.hasOwn(emails, companyId)) {
          const id = Number(companyId);
          if (emails[id]?.length) {
            delete newInputValue[id];
          }
        }
      });
      return newInputValue;
    });
  }, [emails]);

  return {
    emails,
    inputValue,
    handleEmailChange,
    handleAddEmail,
    handleDeleteEmail,
  };
};

export default useManageEmails;
